import { useMemo, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import { isRealTestSlug } from "../util/rideTestSlugs";

// #3353 -- the picker for binding a REAL catalog test to a class or ride whose
// FVD test the catalog could not match (testId `fvd-unmatched:<n>`). Lists the
// WHOLE catalog (the operator knows which test FVD meant; we do not guess).
//
// Shown only while the row is still unmatched, or once an override exists (so
// it can be corrected or cleared). The `fvd-unmatched:9999` sentinel is the
// Test-of-Choice class, which has no single test by design, so it is hidden.
// The backend (score-api handlers/test-binding.js) is the authority and also
// refuses a matched row (409 test_already_matched) and a ride that already has
// a sheet (409 ride_has_sheets); those are surfaced here, not swallowed.
export const TEST_OF_CHOICE_SENTINEL = "fvd-unmatched:9999";

export function shouldShowAssignTest(testId, override) {
  if (testId === TEST_OF_CHOICE_SENTINEL) return false;
  return !isRealTestSlug(testId) || Boolean(override?.testId);
}

const ERROR_KEYS = {
  test_already_matched: "assignTest.errorAlreadyMatched",
  ride_has_sheets: "assignTest.errorRideHasSheets",
};

// The catalog hook (IndexedDB + GET /catalog) only mounts when the panel is
// actually shown, so a matched class/ride never pays for it.
export default function AssignTestPanel(props) {
  if (!shouldShowAssignTest(props.testId, props.override)) return null;
  return <AssignTestPicker {...props} />;
}

function AssignTestPicker({
  idSuffix,
  override,
  onSave,
  disabled = false,
  withReason = false,
}) {
  const { t } = useTranslation("scoreadmin");
  const { catalogDefs, loading } = useCatalogDefs();
  const [picked, setPicked] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState(null);

  const sorted = useMemo(
    () => [...catalogDefs].sort((a, b) => String(a.name ?? a.id).localeCompare(String(b.name ?? b.id))),
    [catalogDefs]
  );

  const run = async (nextTestId) => {
    setBusy(true);
    setErrorKey(null);
    try {
      const trimmed = reason.trim();
      await onSave(nextTestId, withReason && trimmed ? trimmed : undefined);
      setPicked("");
      setReason("");
    } catch (err) {
      setErrorKey(ERROR_KEYS[err?.response?.data?.code] || "assignTest.errorGeneric");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      data-testid={`assign-test-${idSuffix}`}
      sx={{ mt: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
    >
      <Typography variant="subtitle2">{t("assignTest.heading")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {override?.testId
          ? t("assignTest.boundHelp", { testName: override.testName || override.testId })
          : t("assignTest.unmatchedHelp")}
      </Typography>
      <Stack spacing={1.5} sx={{ maxWidth: 420 }}>
        <TextField
          select
          size="small"
          label={t("assignTest.pickerLabel")}
          value={picked}
          disabled={disabled || busy || loading}
          onChange={(e) => setPicked(e.target.value)}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          data-testid={`assign-test-select-${idSuffix}`}
        >
          <option value="">{t("assignTest.pickerPlaceholder")}</option>
          {sorted.map((def) => (
            <option key={def.id} value={def.id}>
              {def.name || def.id}
            </option>
          ))}
        </TextField>
        {withReason && (
          <TextField
            size="small"
            label={t("assignTest.reasonLabel")}
            value={reason}
            disabled={disabled || busy}
            inputProps={{ maxLength: 200 }}
            onChange={(e) => setReason(e.target.value)}
            data-testid={`assign-test-reason-${idSuffix}`}
          />
        )}
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            disabled={disabled || busy || !picked}
            onClick={() => run(picked)}
            data-testid={`assign-test-save-${idSuffix}`}
          >
            {t("assignTest.assign")}
          </Button>
          {override?.testId && (
            <Button
              size="small"
              disabled={disabled || busy}
              onClick={() => run(null)}
              data-testid={`assign-test-clear-${idSuffix}`}
            >
              {t("assignTest.clear")}
            </Button>
          )}
        </Stack>
        {errorKey && (
          <Alert severity="error" data-testid={`assign-test-error-${idSuffix}`}>
            {t(errorKey)}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
